import Box from "@mui/material/Box";
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import DvrIcon from "@mui/icons-material/Dvr";
import { useSelector } from "react-redux";
import { selectUserInfo } from "features/user/userSlice";
import { useForm } from "react-hook-form";
import AlertMessages from "common/Alert/AlertMessages";
import { ClientProfileService } from "subModule/src/core/services";
import Loader from "layout/Loader";
import SimpleModal from "common/Form/Form";
import { Grid, TextField } from "@mui/material";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
interface FormData {
  [key: string]: any;
}
export default function HostingDetailsModal() {
  const params = useParams();
  const navigate = useNavigate();
  const loggedInUserData = useSelector(selectUserInfo);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields, isDirty },
    reset,
  } = useForm<FormData>({
    defaultValues: {},
  });
  const [loading, setLoading] = useState<boolean>(params?.id ? true : false);
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(
    params?.id ? false : true,
  );
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    moduleLabelKey: "hostingDetails",
    moduleHref: "/hosting-details",
    entityFallbackLabelKey: "title",
    isEdit: !!params?.id,
  });
  useEffect(() => {
    if (params?.id) {
      if (loading) getByID();
    }
  }, [params]);
  useEffect(() => {
    if (isDirty && params?.id) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields]);
  const getByID = () => {
    ClientProfileService.getHostingDetailsById(+(params as any)?.id)
      .then((res: any) => {
        if (res.success) {
          reset({ ...(res?.data || {}) });
          captureLabel(res?.data?.title);
          setLoading(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const onSubmit = (data: any) => {
    setIsBtnEnabled(false);
    if (params?.id) {
      ClientProfileService.updateHostingDetails(+params?.id, data)
        .then((response: any) => {
          if (response.success) {
            captureLabel(data?.title);
            navigate(-1);
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("hostingDetails") +
                  " " +
                  t("updated") +
                  " " +
                  t("successfully")
                }
              />,
              { variant: "success" },
            );
          } else {
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
            setIsBtnEnabled(true);
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    } else {
      ClientProfileService.createHostingDetails(data)
        .then((response: any) => {
          if (response.success) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("hostingDetails") +
                  " " +
                  t("created") +
                  " " +
                  t("successfully")
                }
              />,
              { variant: "success" },
            );
            navigate(-1);
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    }
  };
  return (
    <Box>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={true}
        onClick={() => navigate(-1)}
        onSubmit={handleSubmit(onSubmit)}
        heading={
          params?.id ? t("editHostingDetails") : t("createHostingDetails")
        }
        icon={
          <DvrIcon
            sx={{
              fill: "#000",
              width: "30px",
              height: "30px",
              marginTop: "3px",
            }}
          />
        }
        btnText={"back"}
        btnType={"back"}
        isBtnEnabled={IsBtnEnabled}
        showCreateBtn={loggedInUserData?.type === -1}
        breadcrumbItems={breadcrumbItems}
      />
      <Box className={`flex`}>
        {loading ? (
          <Loader />
        ) : (
          <SimpleModal
            visable={true}
            title={
              params?.id ? t("editHostingDetails") : t("createHostingDetails")
            }
            isButtonVisable={true}
            isVisableBtn={IsBtnEnabled}
            btnclosetext="Back"
            buttonText={params?.id ? "update" : "submit"}
            attBtnNotshow={false}
            notVisableBackbtn={true}
            showPortList={handleSubmit(onSubmit)}
            formSubmit={handleSubmit(onSubmit)}
            content={
              <Grid
                container
                component="form"
                id="form"
                rowSpacing={3}
                columnSpacing={2}
                onSubmit={handleSubmit(onSubmit)}
              >
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("title", {
                      validate: {
                        required: (value) =>
                          value.trim() !== "" || t("This field is required."),
                      },
                    })}
                    label={t("title") + " *"}
                    placeholder={t("phHostingTitle")}
                    title={t("title")}
                    fullWidth
                    error={!!errors.title}
                    helperText={errors.title?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("userName", {
                      validate: {
                        required: (value) =>
                          value?.trim() !== "" || t("This field is required."),
                      },
                    })}
                    label={t("userName") + " *"}
                    placeholder={t("phHostingUserName")}
                    title={t("userName")}
                    fullWidth
                    error={!!errors.userName}
                    helperText={errors.userName?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("password", {
                      validate: {
                        required: (value) =>
                          value?.trim() !== "" || t("This field is required."),
                      },
                    })}
                    label={t("password") + " *"}
                    placeholder={t("phHostingPassword")}
                    title={t("password")}
                    fullWidth
                    error={!!errors.password}
                    helperText={errors.password?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("url", {
                      validate: {
                        required: (value) =>
                          value?.trim() !== "" || t("This field is required."),
                      },
                    })}
                    label={t("url") + " *"}
                    placeholder={t("phHostingUrl")}
                    title={t("url")}
                    fullWidth
                    error={!!errors.password}
                    helperText={errors.password?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("description")}
                    label={t("description")}
                    placeholder={t("phHostingDescription")}
                    title={t("description")}
                    fullWidth
                    error={!!errors.description}
                    helperText={errors.description?.message as string}
                    InputLabelProps={{ shrink: true }}
                    multiline
                    rows={1}
                    sx={{
                      "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                        width: "100%",
                        paddingRight: "0px",
                        paddingBottom: 0,
                      },
                      "& .MuiInputBase-inputMultiline": {
                        resize: "vertical",
                        minHeight: "29px",
                      },
                    }}
                  />
                </Grid>
              </Grid>
            }
          />
        )}
      </Box>
    </Box>
  );
}
